import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO, isBefore, startOfToday, addDays } from 'date-fns';
import IconButton from '../../../../components/IconButton';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../../components/IconButton/icons';
import { TimesheetBatch } from '../../config';
import './week-selection.scss';

interface WeekSelectionProps {
  selectedWeek: string;
  onWeekSelect: (weekEndingDate: string) => void;
  availableBatches: Pick<TimesheetBatch, 'id' | 'week_ending_date' | 'status'>[];
}

/**
 * The scrolling strip of week chips. Most recent on the left. A chip carries
 * its status as a dot (draft none, submitted blue, approved green) and a red
 * ring when its week has ended and it is still a draft.
 */
const WeekSelection: React.FC<WeekSelectionProps> = ({ selectedWeek, onWeekSelect, availableBatches }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  const updateArrows = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows);
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [availableBatches, updateArrows]);

  // Keep the selected chip in view when the selection changes.
  useEffect(() => {
    const el = scroller.current?.querySelector<HTMLElement>('.week-chip.is-selected');
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedWeek]);

  const scrollBy = (dx: number) => scroller.current?.scrollBy({ left: dx, behavior: 'smooth' });

  const today = startOfToday();
  const sorted = [...availableBatches].sort((a, b) => b.week_ending_date.localeCompare(a.week_ending_date));

  return (
    <div className="week-selection">
      <IconButton
        icon={<ChevronLeftIcon />}
        label="Earlier weeks"
        size="sm"
        disabled={!canScrollLeft}
        onClick={() => scrollBy(-240)}
      />
      <div className="week-selection__scroller" ref={scroller}>
        <div className="week-chips" role="tablist" aria-label="Weeks">
          {sorted.map((batch) => {
            const end = parseISO(batch.week_ending_date);
            const start = addDays(end, -6);
            const status = batch.status.toLowerCase();
            const overdue = status === 'draft' && isBefore(end, today);
            const current = !isBefore(end, today) && !isBefore(today, start);
            const selected = batch.week_ending_date === selectedWeek;
            return (
              <button
                key={batch.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`week-chip ${selected ? 'is-selected' : ''} ${overdue ? 'is-overdue' : ''} ${current ? 'is-current' : ''}`}
                title={overdue ? `${batch.status}: this week has ended and is not submitted` : batch.status}
                onClick={() => onWeekSelect(batch.week_ending_date)}
              >
                <span className={`week-chip__dot week-chip__dot--${status}`} aria-hidden="true" />
                <span className="week-chip__label">
                  {format(start, 'd MMM')} – {format(end, 'd MMM')}
                </span>
                {overdue && (
                  <span className="week-chip__overdue" aria-label="Not submitted">
                    !
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <IconButton
        icon={<ChevronRightIcon />}
        label="Later weeks"
        size="sm"
        disabled={!canScrollRight}
        onClick={() => scrollBy(240)}
      />
    </div>
  );
};

export default WeekSelection;
